import React from "react"

import { styled } from "@mui/material/styles"

import { useTranslations } from "@cirrus/core/i18n/provider"

import { CommentForm } from "./components/CommentForm"
import { Message } from "./components/Message"
import { IComment } from "./types/IComment"
import { IPostComment } from "./types/IPostComment"
import { NoContent } from "../NoContent"

interface IProps<T extends IComment<T, TMention, TRole>, TMention, TRole> {
  items: T[],
  placeholder: string,
  onSend: (comment: IPostComment) => void,
  onToggleLike: (id: string) => void,
  onDelete: (id: string) => void,
  lockText?: string,
  getCommentRole?: (comment: T) => string,
}

export const Comments = <T extends IComment<T, TMention, TRole>, TMention, TRole>({
  items,
  placeholder,
  onSend,
  onToggleLike,
  onDelete,
  lockText,
  getCommentRole,
}: IProps<T, TMention, TRole>) => {
  const {i18n} = useTranslations()

  return (
    <Wrapper>
      {
        (items || []).length === 0
          ? <NoContent message={i18n("Comments:NotAvailable")}
                       description={i18n("Comments:NotAvailableDescription")}/>
          : <MessagesWrapper>
            {items
              .map((item) =>
                <Message key={item.id}
                         toggleLike={onToggleLike}
                         onDelete={onDelete}
                         onSend={onSend}
                         getCommentRole={getCommentRole}
                         {...item} />)}
          </MessagesWrapper>
      }

      <CommentForm lockText={lockText} onSend={onSend} placeholder={placeholder} />
    </Wrapper>
  )
}

const MessagesWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "20px",

  overflowY: "auto",
})

const Wrapper = styled("div")({
  display: "grid",
  gridTemplateRows: "1fr auto",
  gap: "20px",
  height: "100%",
  overflow: "auto"
})
